/**
 * Chat Service - Handles all messaging and chat operations
 * Including sending messages, managing conversations, and real-time updates
 */

import { database } from './firebase';
import {
  ref,
  set,
  get,
  update,
  remove,
  onValue,
  off,
  serverTimestamp,
  push,
  query,
  orderByChild,
  limitToLast,
  increment
} from 'firebase/database';

/**
 * Generates a conversation ID from two user IDs
 * Always returns the same ID regardless of order (alphabetical)
 *
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @returns {string} Conversation ID
 */
export const getConversationId = (userId1, userId2) => {
  return [userId1, userId2].sort().join('_');
};

/**
 * Sends a message to another user
 *
 * @param {string} fromUserId - Sender's user ID
 * @param {string} fromUserName - Sender's display name
 * @param {string} toUserId - Recipient's user ID
 * @param {string} toUserName - Recipient's display name
 * @param {string} message - Message content
 * @param {string} roomCode - Optional room code if message is sent in a room context
 * @param {string} fromUserPhoto - Sender's photo URL
 * @param {string} toUserPhoto - Recipient's photo URL
 * @returns {Promise<string>} The message ID
 */
export const sendMessage = async (
  fromUserId,
  fromUserName,
  toUserId,
  toUserName,
  message,
  roomCode = null,
  fromUserPhoto = null,
  toUserPhoto = null
) => {
  try {
    const conversationId = getConversationId(fromUserId, toUserId);
    const messagesRef = ref(database, `messages/${conversationId}`);
    const newMessageRef = push(messagesRef);

    const messageData = {
      id: newMessageRef.key,
      from: fromUserId,
      fromName: fromUserName,
      to: toUserId,
      toName: toUserName,
      message: message.trim(),
      timestamp: Date.now(),
      createdAt: serverTimestamp(),
      read: false,
      type: 'text',
      roomCode: roomCode
    };

    await set(newMessageRef, messageData);

    // Update conversation metadata for both users
    await updateConversationMetadata(
      fromUserId,
      toUserId,
      fromUserName,
      toUserName,
      message,
      Date.now(),
      fromUserPhoto,
      toUserPhoto
    );

    // Message notifications are now handled by the MessagingCenter badge
    // No need to send to NotificationCenter

    return newMessageRef.key;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Updates conversation metadata for both users
 *
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @param {string} userName1 - First user's display name
 * @param {string} userName2 - Second user's display name
 * @param {string} lastMessage - Last message preview
 * @param {number} timestamp - Message timestamp
 * @param {string} userPhoto1 - First user's photo URL
 * @param {string} userPhoto2 - Second user's photo URL
 * @returns {Promise<void>}
 */
const updateConversationMetadata = async (
  userId1,
  userId2,
  userName1,
  userName2,
  lastMessage,
  timestamp,
  userPhoto1 = null,
  userPhoto2 = null
) => {
  try {
    const conversationId = getConversationId(userId1, userId2);

    // Update for user1 (sender - they read their own messages, so unreadCount stays at 0)
    const user1ConversationRef = ref(
      database,
      `conversations/${userId1}/${userId2}`
    );
    await update(user1ConversationRef, {
      conversationId,
      userId: userId2,
      displayName: userName2,
      photoURL: userPhoto2,
      lastMessage: lastMessage.substring(0, 100),
      lastMessageTime: timestamp,
      updatedAt: serverTimestamp(),
      unreadCount: 0
    });

    // Update for user2 (recipient - increment their unread count)
    const user2ConversationRef = ref(
      database,
      `conversations/${userId2}/${userId1}`
    );

    await update(user2ConversationRef, {
      conversationId,
      userId: userId1,
      displayName: userName1,
      photoURL: userPhoto1,
      lastMessage: lastMessage.substring(0, 100),
      lastMessageTime: timestamp,
      updatedAt: serverTimestamp(),
      unreadCount: increment(1)  // Atomically increment by 1
    });
  } catch (error) {
    console.error('Error updating conversation metadata:', error);
    throw error;
  }
};

/**
 * Marks all messages in a conversation as read
 *
 * @param {string} userId - Current user's ID
 * @param {string} otherUserId - Other user's ID
 * @returns {Promise<void>}
 */
export const markConversationAsRead = async (userId, otherUserId) => {
  try {
    const conversationId = getConversationId(userId, otherUserId);
    const messagesRef = ref(database, `messages/${conversationId}`);
    const snapshot = await get(messagesRef);

    if (!snapshot.exists()) {
      return;
    }

    const updates = {};
    snapshot.forEach((childSnapshot) => {
      const message = childSnapshot.val();
      // Only mark messages sent TO the current user as read
      if (message.to === userId && !message.read) {
        updates[`${childSnapshot.key}/read`] = true;
        updates[`${childSnapshot.key}/readAt`] = serverTimestamp();
      }
    });

    if (Object.keys(updates).length > 0) {
      await update(messagesRef, updates);
    }

    // Reset unread count to 0
    const conversationRef = ref(database, `conversations/${userId}/${otherUserId}`);
    await update(conversationRef, {
      unreadCount: 0
    });
  } catch (error) {
    console.error('Error marking conversation as read:', error);
    throw error;
  }
};

/**
 * Gets all messages in a conversation
 *
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @param {number} limit - Maximum number of messages to retrieve
 * @returns {Promise<Array>} Array of messages
 */
export const getMessages = async (userId1, userId2, limit = 100) => {
  try {
    const conversationId = getConversationId(userId1, userId2);
    const messagesRef = ref(database, `messages/${conversationId}`);
    const messagesQuery = query(
      messagesRef,
      orderByChild('timestamp'),
      limitToLast(limit)
    );

    const snapshot = await get(messagesQuery);

    if (!snapshot.exists()) {
      return [];
    }

    const messages = [];
    snapshot.forEach((childSnapshot) => {
      messages.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    return messages;
  } catch (error) {
    console.error('Error getting messages:', error);
    throw error;
  }
};

/**
 * Subscribes to real-time messages in a conversation
 *
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @param {Function} callback - Function to call when messages change
 * @returns {Function} Unsubscribe function
 */
export const subscribeToMessages = (userId1, userId2, callback) => {
  const conversationId = getConversationId(userId1, userId2);
  const messagesRef = ref(database, `messages/${conversationId}`);

  const unsubscribe = onValue(messagesRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }

    const messages = [];
    snapshot.forEach((childSnapshot) => {
      messages.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Sort by timestamp (oldest first for chat display)
    messages.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

    callback(messages);
  });

  return () => off(messagesRef, 'value', unsubscribe);
};

/**
 * Gets all conversations for a user
 *
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of conversations
 */
export const getConversations = async (userId) => {
  try {
    const conversationsRef = ref(database, `conversations/${userId}`);
    const snapshot = await get(conversationsRef);

    if (!snapshot.exists()) {
      return [];
    }

    const conversations = [];
    snapshot.forEach((childSnapshot) => {
      conversations.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Sort by last message time (newest first)
    conversations.sort((a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0));

    return conversations;
  } catch (error) {
    console.error('Error getting conversations:', error);
    throw error;
  }
};

/**
 * Subscribes to real-time conversation updates
 *
 * @param {string} userId - User ID
 * @param {Function} callback - Function to call when conversations change
 * @returns {Function} Unsubscribe function
 */
export const subscribeToConversations = (userId, callback) => {
  const conversationsRef = ref(database, `conversations/${userId}`);

  const unsubscribe = onValue(conversationsRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }

    const conversations = [];
    snapshot.forEach((childSnapshot) => {
      conversations.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Sort by last message time (newest first)
    conversations.sort((a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0));

    callback(conversations);
  });

  return () => off(conversationsRef, 'value', unsubscribe);
};

/**
 * Sets typing indicator status
 *
 * @param {string} userId - User who is typing
 * @param {string} otherUserId - User to notify
 * @param {boolean} isTyping - Typing status
 * @returns {Promise<void>}
 */
export const setTypingStatus = async (userId, otherUserId, isTyping) => {
  try {
    const conversationId = getConversationId(userId, otherUserId);
    const typingRef = ref(database, `typing/${conversationId}/${userId}`);

    if (isTyping) {
      await set(typingRef, {
        userId,
        timestamp: serverTimestamp()
      });

      // Auto-clear after 3 seconds
      setTimeout(async () => {
        await remove(typingRef);
      }, 3000);
    } else {
      await remove(typingRef);
    }
  } catch (error) {
    console.error('Error setting typing status:', error);
  }
};

/**
 * Subscribes to typing indicators
 *
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @param {Function} callback - Function to call when typing status changes
 * @returns {Function} Unsubscribe function
 */
export const subscribeToTyping = (userId1, userId2, callback) => {
  const conversationId = getConversationId(userId1, userId2);
  const typingRef = ref(database, `typing/${conversationId}/${userId2}`);

  const unsubscribe = onValue(typingRef, (snapshot) => {
    callback(snapshot.exists());
  });

  return () => off(typingRef, 'value', unsubscribe);
};

/**
 * Deletes a message
 *
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @param {string} messageId - Message ID to delete
 * @returns {Promise<void>}
 */
export const deleteMessage = async (userId1, userId2, messageId) => {
  try {
    const conversationId = getConversationId(userId1, userId2);
    const messageRef = ref(database, `messages/${conversationId}/${messageId}`);
    await remove(messageRef);
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

/**
 * Deletes an entire conversation
 *
 * @param {string} userId - Current user's ID
 * @param {string} otherUserId - Other user's ID
 * @returns {Promise<void>}
 */
export const deleteConversation = async (userId, otherUserId) => {
  try {
    // Remove conversation metadata for current user only
    const conversationRef = ref(database, `conversations/${userId}/${otherUserId}`);
    await remove(conversationRef);
  } catch (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
};

/**
 * Gets the total unread message count for a user
 *
 * @param {string} userId - User ID
 * @returns {Promise<number>} Total unread count
 */
export const getTotalUnreadCount = async (userId) => {
  try {
    const conversationsRef = ref(database, `conversations/${userId}`);
    const snapshot = await get(conversationsRef);

    if (!snapshot.exists()) {
      return 0;
    }

    let total = 0;
    snapshot.forEach((childSnapshot) => {
      const conversation = childSnapshot.val();
      total += conversation.unreadCount || 0;
    });

    return total;
  } catch (error) {
    console.error('Error getting total unread count:', error);
    return 0;
  }
};
